import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Calendar, TrendingUp, MessageCircle, Heart, ArrowLeft, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const ChildDashboard = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [newNote, setNewNote] = useState({
    session_date: new Date().toISOString().split('T')[0],
    notes: '',
    goals_discussed: [],
    action_items: []
  });
  const [newGoal, setNewGoal] = useState('');
  const [newAction, setNewAction] = useState('');

  useEffect(() => {
    loadData();
  }, [childId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [progressRes, notesRes] = await Promise.all([
        API.get(`/children/${childId}/progress?days=30`),
        API.get(`/coaching-notes/${childId}`)
      ]);
      setProgress(progressRes.data);
      setNotes(notesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load child dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      await API.post('/coaching-notes', {
        child_id: childId,
        ...newNote
      });
      toast.success('Session note added!');
      setAddNoteOpen(false);
      setNewNote({
        session_date: new Date().toISOString().split('T')[0],
        notes: '',
        goals_discussed: [],
        action_items: []
      });
      loadData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const addGoalToNote = () => {
    if (newGoal.trim()) {
      setNewNote(prev => ({
        ...prev,
        goals_discussed: [...prev.goals_discussed, newGoal.trim()]
      }));
      setNewGoal('');
    }
  };

  const addActionToNote = () => {
    if (newAction.trim()) {
      setNewNote(prev => ({
        ...prev,
        action_items: [...prev.action_items, newAction.trim()]
      }));
      setNewAction('');
    }
  };

  const removeFromArray = (field, index) => {
    setNewNote(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Child not found</p>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const moodColors = {
    happy: 'bg-green-500',
    calm: 'bg-blue-500',
    anxious: 'bg-yellow-500',
    upset: 'bg-red-500',
    excited: 'bg-purple-500'
  };

  return (
    <div className="space-y-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold">{progress.child_name}'s Progress</h1>
            <p className="text-muted-foreground">Tracking {progress.days_tracked} days of progress</p>
          </div>
        </div>
        <Dialog open={addNoteOpen} onOpenChange={setAddNoteOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Session Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Coaching Session Note</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddNote} className="space-y-4">
              <div>
                <Label htmlFor="session_date">Session Date</Label>
                <Input
                  id="session_date"
                  type="date"
                  value={newNote.session_date}
                  onChange={(e) => setNewNote({ ...newNote, session_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="notes">Session Notes</Label>
                <Textarea
                  id="notes"
                  rows={6}
                  placeholder="What did we discuss? What progress was made?"
                  value={newNote.notes}
                  onChange={(e) => setNewNote({ ...newNote, notes: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Goals Discussed</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newNote.goals_discussed.map((goal, idx) => (
                    <Badge key={idx} className="pl-3 pr-2">
                      {goal}
                      <button type="button" onClick={() => removeFromArray('goals_discussed', idx)} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a goal"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGoalToNote())}
                  />
                  <Button type="button" variant="outline" onClick={addGoalToNote}>
                    Add
                  </Button>
                </div>
              </div>
              <div>
                <Label>Action Items</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newNote.action_items.map((action, idx) => (
                    <Badge key={idx} variant="outline" className="pl-3 pr-2">
                      {action}
                      <button type="button" onClick={() => removeFromArray('action_items', idx)} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add an action item"
                    value={newAction}
                    onChange={(e) => setNewAction(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addActionToNote())}
                  />
                  <Button type="button" variant="outline" onClick={addActionToNote}>
                    Add
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full">Save Session Note</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-2">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{progress.total_logs}</CardTitle>
            <CardDescription>Days Logged</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-2">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{progress.avg_communication_per_day}</CardTitle>
            <CardDescription>Avg Communication/Day</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-2">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{progress.recent_wins.length}</CardTitle>
            <CardDescription>Recent Wins</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-2">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">{progress.coaching_sessions}</CardTitle>
            <CardDescription>Coaching Sessions</CardDescription>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="progress">
        <TabsList>
          <TabsTrigger value="progress">Daily Progress</TabsTrigger>
          <TabsTrigger value="notes">Coaching Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="progress" className="space-y-6">
          {/* Mood Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Mood Distribution</CardTitle>
              <CardDescription>Last {progress.days_tracked} days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(progress.mood_distribution).map(([mood, count]) => (
                  <div key={mood} className="flex items-center gap-4">
                    <div className="w-24 capitalize font-medium">{mood}</div>
                    <div className="flex-1">
                      <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${moodColors[mood] || 'bg-gray-400'} flex items-center justify-end pr-3 text-white text-sm font-semibold`}
                          style={{
                            width: `${(count / progress.total_logs) * 100}%`,
                            minWidth: count > 0 ? '40px' : '0'
                          }}
                        >
                          {count}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Behavior Tracking */}
          <Card>
            <CardHeader>
              <CardTitle>Behavior Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(progress.behavior_counts).map(([behavior, count]) => (
                  <Badge key={behavior} variant="outline" className="px-4 py-2 text-sm">
                    {behavior.replace('_', ' ')}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Wins */}
          {progress.recent_wins.length > 0 && (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-green-600" />
                  Recent Wins
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {progress.recent_wins.map((win, idx) => (
                    <Badge key={idx} className="bg-green-600">
                      {win}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Logs */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Daily Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {progress.logs && progress.logs.map((log, idx) => (
                  <Card key={idx} className="bg-gray-50">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{log.date}</span>
                        <Badge className="capitalize">{log.child_mood}</Badge>
                      </div>
                      {log.notes && (
                        <p className="text-sm text-muted-foreground">{log.notes}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Link to="/tracker">
                  <Button variant="outline">View All Logs</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4">
          {notes.length > 0 ? (
            notes.map((note) => (
              <Card key={note.note_id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Session: {note.session_date}</CardTitle>
                      <CardDescription>
                        {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })} • 
                        Added by {note.created_by === 'admin' ? 'Robin (Coach)' : 'You'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="whitespace-pre-wrap">{note.notes}</p>
                  </div>
                  {note.goals_discussed && note.goals_discussed.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-2">Goals Discussed:</p>
                      <div className="flex flex-wrap gap-2">
                        {note.goals_discussed.map((goal, idx) => (
                          <Badge key={idx} variant="default">{goal}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {note.action_items && note.action_items.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-2">Action Items:</p>
                      <div className="flex flex-wrap gap-2">
                        {note.action_items.map((action, idx) => (
                          <Badge key={idx} variant="outline">{action}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground mb-4">No coaching session notes yet</p>
                <Button onClick={() => setAddNoteOpen(true)}>Add Your First Note</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChildDashboard;